import { Box, Stack, Theme, Typography, alpha } from '@mui/material';

interface Props {
  selected: boolean;
  onClick: () => void;
  title: string;
  description: string;
  aside?: string;
}

function OptionItem({ selected, onClick, title, description, aside }: Props) {
  return (
    <Box
      component="button"
      type="button"
      role="radio"
      aria-checked={selected}
      onClick={onClick}
      sx={(theme: Theme) => ({
        position: 'relative',
        display: 'grid',
        gridTemplateColumns: 'auto 1fr auto',
        alignItems: 'center',
        gap: 2.25,
        p: '22px 24px',
        background: '#fff',
        border: `1px solid ${
          selected
            ? theme.palette.primary.main
            : theme.palette.darkerLightGrey.main
        }`,
        borderRadius: '14px',
        cursor: 'pointer',
        textAlign: 'left',
        fontFamily: 'inherit',
        color: 'text.primary',
        transition: theme.transitions.create(
          ['border-color', 'background-color', 'box-shadow', 'transform'],
          { duration: 200 },
        ),
        ...(selected && {
          background: '#fff6f4',
          boxShadow: `0 6px 18px -14px ${alpha(
            theme.palette.primary.main,
            0.22,
          )}`,
        }),
        '&:hover': {
          borderColor: selected
            ? theme.palette.primary.main
            : theme.palette.outlineGrey.main,
          transform: selected ? 'none' : 'translateY(-1px)',
          boxShadow: selected
            ? `0 6px 18px -14px ${alpha(theme.palette.primary.main, 0.22)}`
            : '0 12px 32px -20px rgba(51,51,51,0.22)',
        },
        '&:focus-visible': {
          outline: `2px solid ${theme.palette.primary.main}`,
          outlineOffset: '2px',
        },
        [theme.breakpoints.down(720)]: {
          gridTemplateColumns: 'auto 1fr',
          p: '18px',
          gap: '14px',
        },
      })}>
      <Box
        aria-hidden
        sx={(theme: Theme) => ({
          width: 22,
          height: 22,
          borderRadius: '50%',
          border: `1.5px solid ${
            selected
              ? theme.palette.primary.main
              : theme.palette.darkerLightGrey.main
          }`,
          background: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          transition: theme.transitions.create(
            ['border-color', 'background-color'],
            { duration: 200 },
          ),
          '&::after': {
            content: '""',
            width: 8,
            height: 8,
            borderRadius: '50%',
            background: selected ? theme.palette.primary.main : 'transparent',
            transition: theme.transitions.create(['background-color'], {
              duration: 200,
            }),
          },
        })}
      />
      <Stack gap={0.5} minWidth={0}>
        <Typography variant="h4" sx={{ lineHeight: 1.3 }}>
          {title}
        </Typography>
        <Typography
          variant="body2"
          sx={() => ({
            color: 'text.secondary',
            lineHeight: 1.5,
          })}>
          {description}
        </Typography>
      </Stack>
      {aside ? (
        <Typography
          variant="overline"
          sx={theme => ({
            color: selected
              ? theme.palette.primary.main
              : theme.palette.outlineGrey.main,
            whiteSpace: 'nowrap',
            [theme.breakpoints.down(720)]: { display: 'none' },
          })}>
          {aside}
        </Typography>
      ) : (
        <Box />
      )}
    </Box>
  );
}

export default OptionItem;
